import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, filter, last, map, mapTo, switchMap, take, tap } from 'rxjs/operators';
import { AppSettingsService, IToken, LoggerService, OI_URL, RestClientBaseService } from '@nx-fe/services';
import {
  AppCategory,
  AppMappings,
  ApplicationConfigWithPages,
  BatchImportJobResultModel,
  InitialData,
  WebAppCategory,
  WebAppMapping,
} from '../../shared/features/ng-redux/app/appConfig.models';
import { DashboardConfigMapper } from '../mappers/dashboard-config-mapper';

import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import {
  BatteryPool,
  BatteryPoolConfiguration,
  BatteryPoolOrder,
} from '../../shared/features/ng-redux/battery-pool/battery-pool.reducer';
import { IProfile, IProfileConfiguration } from '../../modules/analytics-profiles/profile-contract';
import { SbDecommission, Shifts } from '../../modules/battery-pool/models/models';
import { AgentConfig, PcgVersion } from '../../modules/pcg-generator/models/Agent-pcg';
import { DataSourceType, DatasourceConfig } from '../../shared/features/ng-redux/datasource/datasource.models';
import {
  McGroup,
  McInstance,
  Profile,
  ProfileConfiguration,
} from '../../shared/features/ng-redux/profiles/profiles.models';
import { ProfilesMapper } from '../mappers/profiles-mapper';
import {
  ChartDataResponse,
  CustomAttribute,
  CustomAttributeValue,
  DataRequest,
  DeviceDataResponse,
} from './dataRequest';

import {
  SharedTopicInfo,
  WLMcGroup,
  WatchlistConfig,
  WatchlistProfile,
  WatchlistDeletedTopic,
} from '../../../app/modules/icu/models/watch-profile.model';
import { mapConfigToWatchlistProfile } from '../../../app/modules/icu/utils/watchlist-config-mapper';
import { WLSnoozedData, WatchlistSnoozeDevice } from '../../shared/features/ng-redux/watch-list/watchlist.model';
import {
  UtilizationProfileRequest,
  UtilizationMcGroup,
  UtilizationProfile,
  UtilizationCustomAttributes,
  UtilizationWallMappings,
} from '../../modules/utilization/models/utilization.model';
import { SharedTopicBundle } from '@nx-fe/manage-shared-topic';
import { UtilizationCustomAttributeSelection } from '../../modules/utilization/models/utilization-form.model';
import { UtilizationStores } from 'shared/features/ng-redux/page/page.models';

export interface RatedCapacity {
  modelId: number;
  modelName: string;
  config: object;
}

function isHttpEvent(
  value: ChartDataResponse[] | HttpEvent<ChartDataResponse[]>
): value is HttpEvent<ChartDataResponse[]> {
  return (<any>value).type !== undefined;
}
/**
 * The API view of the dashboard configuration
 */

export interface DashboardConfigView {
  apps: ApplicationConfigWithPages[];
  dataSources?: DatasourceConfig[];
  profiles: IProfile[];
  pools: BatteryPool[];
  shifts: Shifts[];
  appCategories: AppCategory[];
  watchlistProfiles: WatchlistProfile[];
  utilizationProfiles: UtilizationProfile[];
}

export interface BatteryInfo {
  mfgBatteryId: string;
  partNo: string;
  serialNumber: string;
  manufactureDate: string;
  manufacturerName: string;
  decomissionedOn: string;
}

export interface ThresholdConfig {
  id: number;
  valueFrom: number;
  valueTo: number;
  label?: string;
  color?: string;
}

export interface StrengthThresholdConfig {
  rangeId: number;
  buckets: ThresholdConfig[];
}

export interface TemperatureConfig {
  tempCold: number;
  tempHot: number;
}

export interface BatteryImportResult {
  rowsImported: number;
  rowsInserted: number;
}

@Injectable()
export class DidRestClientService extends RestClientBaseService {
  constructor(
    httpClient: HttpClient,
    appSettingsService: AppSettingsService,
    loggerService: LoggerService,
    private translate: TranslateService
  ) {
    super(httpClient, appSettingsService, loggerService);
  }

  // temporary check and swap until language code is changed in db
  getDbLangCode(language: string): string {
    switch (language) {
      case 'ja-JP':
        return 'jp_JP';
      default:
        return 'en_US';
    }
  }

  // Refresh token from Assist OAuth
  refreshSession(): Observable<IToken> {
    const params = {
      grantType: 'refresh_token',
      clientId: this.appSettingsService.clientId,
      scope: 'openid, profile, offline_access',
    };

    return this.http.post<IToken>(`${this.appSettingsService.accountServiceUrl}login`, this.getQueryString(params), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  }

  revokeCookie(): Observable<boolean> {
    return this.http.post(this.appSettingsService.accountServiceUrl + 'logout', null).pipe(
      map(_ => true),
      catchError(_ => of(false))
    );
  }

  checkForDuplicateWatchlistName(wlName: string): Promise<boolean> {
    return firstValueFrom(this.http.get<any>(`${OI_URL}/watchlist/duplicateNameCheck/${wlName}`));
  }

  private getQueryString(parameters: { [key: string]: any }): string {
    let delimiter = '';
    let params = '';
    for (const parameterName in parameters) {
      if (parameters.hasOwnProperty(parameterName)) {
        const parameterValue = parameters[parameterName];
        if (parameterValue !== undefined) {
          params += delimiter + encodeURIComponent(parameterName) + '=' + encodeURIComponent(parameterValue);
          delimiter = '&';
        }
      }
    }
    return params;
  }

  getConfig(): Observable<DashboardConfigView> {
    window.addTiming && window.addTiming('rest:getConfig');
    return this.http.get<DashboardConfigView>(`${OI_URL}/dashboard/config`).pipe(
      map(cfg => ({
        ...cfg,
        dataSources: [
          <DatasourceConfig>{
            id: 'iamamockdatasource',
            type: 'InsightAndroidAgent',
            name: 'mock ds',
            status: 'Active',
            createdAt: null,
            modifiedAt: null,
          },
        ],
      })),
      map(cfg => ({
        dataSources: cfg.dataSources,
        apps: DashboardConfigMapper.mapAppConfigs(cfg.apps),
        profiles: cfg.profiles,
        pools: cfg.pools,
        shifts: cfg.shifts,
        appCategories: cfg.appCategories,
        watchlistProfiles: cfg.watchlistProfiles,
        utilizationProfiles: cfg.utilizationProfiles,
      })),
      tap({ next: () => window.endTiming && window.endTiming('rest:getConfig') })
    );
  }

  getWatchMcGroups(instanceId: number, osId: number = 0): Observable<WLMcGroup[]> {
    window.addTiming && window.addTiming(`rest:getWLMcGroups:${instanceId}`);
    const url = `${OI_URL}/watchlist/groups/${instanceId}/${osId}`;
    return this.http
      .get<WLMcGroup[]>(url)
      .pipe(tap({ next: () => window.endTiming && window.endTiming(`rest:getWLMcGroups:${instanceId}`) }));
  }

  getWLSharedTopics(groupIds: string): Observable<SharedTopicInfo[]> {
    const filter = { groupIds };
    const url = `${OI_URL}/watchlist/topics/shared/list`;
    return this.http.post<SharedTopicInfo[]>(url, filter);
  }

  getWLProfileDeletedTopics(wId: number): Observable<WatchlistDeletedTopic[]> {
    return this.http.get<WatchlistDeletedTopic[]>(`${OI_URL}/watchlist/config/${wId}/deletedTopics`);
  }

  updateWatchConfig(watchConfig: WatchlistConfig): Observable<WatchlistProfile> {
    const url = `${OI_URL}/watchlist/update`;
    const watchProfile = mapConfigToWatchlistProfile(watchConfig);
    return this.http.post<number>(url, watchConfig).pipe(
      map(wId => ({
        ...watchProfile,
        WatchListId: wId,
      }))
    );
  }

  getWatchConfigs(): Observable<WatchlistProfile[]> {
    return this.http.get<WatchlistProfile[]>(`${OI_URL}/watchlist/config`);
  }

  getWatchConfig(wId: number): Observable<WatchlistConfig> {
    return this.http.get<WatchlistConfig>(`${OI_URL}/watchlist/config/${wId}`);
  }

  deleteWatchConfig(wId: number): Observable<any> {
    return this.http.delete(`${OI_URL}/watchlist/${wId}`);
  }

  clearWatchProfileDeletedTopics(wId: number): Observable<any> {
    return this.http.delete(`${OI_URL}/watchlist/config/${wId}/clearDeletedTopicMessages`);
  }

  getCurrentWLConditions(DeviceId: number, watchlistId: number): Observable<any> {
    return this.http.get<any>(`${OI_URL}/watchlist/current/${DeviceId}/${watchlistId}`);
  }

  getDepartedWLConditions(
    deviceId: number,
    watchlistId: number,
    startDate: Date | string,
    endDate: Date | string
  ): Observable<any> {
    const body = {
      deviceId,
      watchlistId,
      startDate,
      endDate,
    };

    return this.http.post<any>(`${OI_URL}/watchlist/departed`, body);
  }

  updateWatchlistSnooze(SnoozedDevices: WatchlistSnoozeDevice[]) {
    return this.http.post(`${OI_URL}/watchlist/snooze`, SnoozedDevices);
  }

  getWatchlistSnoozeList(dataRequest: DataRequest): Observable<WLSnoozedData> {
    return this.http.post<WLSnoozedData>(`${OI_URL}/watchlist/snoozedlist`, dataRequest);
  }

  deleteWatchlistSnoozeList(unsnoozeDevices: WatchlistSnoozeDevice[]) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: unsnoozeDevices,
    };
    return this.http.delete(`${OI_URL}/watchlist/unsnooze`, options);
  }

  getDataSources(type?: DataSourceType): Observable<DatasourceConfig[]> {
    window.addTiming && window.addTiming('rest:getDataSources');
    let url = `${OI_URL}/datasources`;
    if (type) {
      url += '?type=' + type;
    }
    return this.http
      .get<DatasourceConfig[]>(url)
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:getDataSources') }));
  }

  // move to another tab like from memory->battery, then update the memory one.
  updateViewedDeviceState(param: string, payload: {}): Observable<boolean> {
    window.addTiming && window.addTiming('rest:updateViewedDeviceState');
    let url = `${OI_URL}/state/icu`;
    if (param.length > 0) {
      url = url + '?metrickey=' + param;
    }
    return this.http
      .post(url, payload)
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:updateViewedDeviceState') }), mapTo(true));
  }

  getInitialData(): Observable<InitialData> {
    window.addTiming && window.addTiming('rest:getInitialData');
    return this.getConfig().pipe(
      map(config => ({
        applications: config.apps,
        datasources: config.dataSources,
        profiles: config.profiles.map(p => ProfilesMapper.mapToProfile(p)),
        batteryPools: config.pools,
        shifts: config.shifts,
        appCategories: config.appCategories,
        watchlistProfiles: config.watchlistProfiles,
        utilizationProfiles: config.utilizationProfiles,
      })),
      take(1),
      tap({ next: () => window.endTiming && window.endTiming('rest:getInitialData') })
    );
  }

  fetchPageData(pageId: string, dataRequest: DataRequest): Observable<ChartDataResponse[]> {
    window.addTiming && window.addTiming(`rest:fetchPageData:${pageId}`);
    const url = `${OI_URL}/pages/${pageId}/data`;
    let startTime: number = Date.now();
    dataRequest.language = this.getDbLangCode(this.translate.currentLang);

    return this.http
      .post<ChartDataResponse[]>(
        url,
        { ...dataRequest },
        {
          reportProgress: true,
          observe: 'events',
        }
      )
      .pipe(
        filter(event => isHttpEvent(event)),
        tap(event => {
          switch (event.type) {
            case HttpEventType.Sent:
              startTime = Date.now();
              break;
            case HttpEventType.Response:
              window.endTiming && window.endTiming(`rest:fetchPageData:${pageId}`);
              this.logger.debug(
                `FetchPageData ${pageId} = ${event.status} - ${event.statusText} in ${Date.now() - startTime}ms`
              );
              break;
          }
        }),
        filter(event => event.type === HttpEventType.Response),
        map(event => (<HttpResponse<ChartDataResponse[]>>event).body),
        last()
      );
  }

  // Profiles section
  getProfiles(): Observable<Profile[]> {
    window.addTiming && window.addTiming('rest:fetchProfiles');
    const url = `${OI_URL}/profiles`;
    return this.http.get<IProfile[]>(url).pipe(
      map(result => result.map(p => ProfilesMapper.mapToProfile(p))),
      tap({ next: () => window.endTiming && window.endTiming('rest:fetchProfiles') })
    );
  }

  runBatchImportSync(): Observable<void> {
    window.addTiming && window.addTiming('rest:runBatchImport');
    const url = `${OI_URL}/batchimport/sync`;
    return this.http
      .put<void>(url, {})
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:runBatchImport') }));
  }

  getProfile(id: number): Observable<ProfileConfiguration> {
    window.addTiming && window.addTiming('rest:fetchProfile');
    const url = `${OI_URL}/profiles/${id}`;
    return this.http.get<IProfileConfiguration>(url).pipe(
      map(result => ProfilesMapper.mapToConfiguration(result)),
      tap({ next: () => window.endTiming && window.endTiming('rest:fetchProfile') })
    );
  }

  createProfile(profile: ProfileConfiguration): Observable<ProfileConfiguration> {
    window.addTiming && window.addTiming('rest:createProfile');
    //profile['id'] = undefined;
    const request = ProfilesMapper.mapFromConfiguration(profile);
    const url = `${OI_URL}/profiles`;
    return this.http.post<number>(url, request).pipe(
      map(id => ({
        ...profile,
        id,
      })),
      tap({ next: () => window.endTiming && window.endTiming('rest:createProfile') })
    );
  }

  updateProfile(profile: ProfileConfiguration): Observable<ProfileConfiguration> {
    window.addTiming && window.addTiming('rest:updateProfile');
    const request = ProfilesMapper.mapFromConfiguration(profile);
    const url = `${OI_URL}/profiles/`;
    return this.http.post<number>(url, request).pipe(
      switchMap((profileId: number) => this.getProfile(profileId)),
      tap({ next: () => window.endTiming && window.endTiming('rest:updateProfile') })
    );
  }

  updateProfileState(profileId: number, isEnabled: boolean): Observable<any> {
    window.addTiming && window.addTiming('rest:updateProfileState');
    const url = `${OI_URL}/profiles`;
    return this.http
      .post(url, { profileId, isEnabled })
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:updateProfileState') }));
  }

  deleteProfile(profileId: number): Observable<any> {
    window.addTiming && window.addTiming('rest:deleteProfile');
    const url = `${OI_URL}/profiles/${profileId}`;
    return this.http.delete(url).pipe(tap({ next: () => window.endTiming && window.endTiming('rest:deleteProfile') }));
  }

  getMcInstances(): Observable<McInstance[]> {
    window.addTiming && window.addTiming('rest:getMcInstances');
    const url = `${OI_URL}/profiles/mclist`;
    return this.http
      .get<McInstance[]>(url)
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:getMcInstances') }));
  }

  // Smart Battery section
  getBatteryPool(id: number) {
    window.addTiming && window.addTiming('rest:getBatteryPool');
    const url = `${OI_URL}/batterypool/${id}`;
    return this.http
      .get<BatteryPoolConfiguration>(url)
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:getBatteryPool') }));
  }

  orderBatteryPools(poolOrder: BatteryPoolOrder[]) {
    window.addTiming && window.addTiming('rest:createBatteryPool');
    const url = `${OI_URL}/batterypool/order`;
    return this.http
      .post<number>(url, poolOrder)
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:createBatteryPool') }));
  }

  createBatteryPool(batteryPool: BatteryPoolConfiguration) {
    window.addTiming && window.addTiming('rest:createBatteryPool');
    const url = `${OI_URL}/batterypool`;
    return this.http.post<number>(url, batteryPool).pipe(
      map(id => {
        return { ...batteryPool, poolId: id };
      }),
      tap({ next: () => window.endTiming && window.endTiming('rest:createBatteryPool') })
    );
  }

  updateBatteryPool(batteryPool: BatteryPoolConfiguration) {
    window.addTiming && window.addTiming('rest:updateBatteryPool');
    const url = `${OI_URL}/batterypool`;
    return this.http.post<number>(url, batteryPool).pipe(
      map(_ => batteryPool),
      tap({ next: () => window.endTiming && window.endTiming('rest:updateBatteryPool') })
    );
  }

  deleteBatteryPool(poolId: number): Observable<any> {
    window.addTiming && window.addTiming('rest:deleteBatteryPool');
    const url = `${OI_URL}/batterypool/${poolId}`;
    return this.http
      .delete(url)
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:deleteBatteryPool') }));
  }

  getLatestDevices(batteryId: number): Observable<any> {
    const url = `${OI_URL}/batterypool/batteries/${batteryId}/devices`;
    return this.http.get<any>(url);
  }

  getLatestBatteries(McDeviceId: string): Observable<any> {
    const url = `${OI_URL}/batterypool/devices/${McDeviceId}/batteries`;
    return this.http.get<any>(url);
  }

  getBatteryInfo(batteryId: number): Observable<BatteryInfo> {
    const url = `${OI_URL}/batterypool/batteries/${batteryId}`;
    return this.http.get<BatteryInfo>(url);
  }

  importBatteryPool(file: File, poolId: number): Observable<BatteryImportResult[]> {
    window.addTiming && window.addTiming('rest:importBatteryPool');
    const formData = new FormData();
    formData.append('uploadedFile', file, file.name);
    const url = `${OI_URL}/batterypool/${poolId}/batteries/csvUpload`;
    return this.http
      .post<any>(url, formData)
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:importBatteryPool') }));
  }

  /**
   * New API for getting all groups by MC instance
   *
   * @param mcInstanceId
   * @returns
   */
  getMcGroups(instanceId: number, osId: number = 0): Observable<McGroup[]> {
    window.addTiming && window.addTiming(`rest:getMcGroups:${instanceId}`);
    const url = `${OI_URL}/profiles/groups/${instanceId}/${osId}`;
    return this.http
      .get<McGroup[]>(url)
      .pipe(tap({ next: () => window.endTiming && window.endTiming(`rest:getMcGroups:${instanceId}`) }));
  }

  getPcgVersions(): Observable<PcgVersion[]> {
    const url = `${OI_URL}/pcg/versions`;
    return this.http.get<PcgVersion[]>(url);
  }

  getAgentConnectionConfig(): Observable<AgentConfig> {
    const url = `${OI_URL}/pcg/agentconfig`;
    return this.http.get<AgentConfig>(url);
  }

  setAgentConnectionConfig(data): Observable<any> {
    const url = `${OI_URL}/pcg/agentconfig`;
    return this.http.post<AgentConfig>(url, data);
  }

  getAgentProxyConfig(platformType): Observable<AgentConfig> {
    const url = `${OI_URL}/pcg/agentconfig/proxyconfig/${platformType}`;
    return this.http.get<AgentConfig>(url);
  }

  downloadPCGAndSave(data, options: any): Observable<any> {
    const url = `${OI_URL}/pcg/download`;
    return this.http.post<Blob>(url, data, options);
  }

  uploadPCG(data, options?: any): Observable<any> {
    const url = `${OI_URL}/pcg/package`;
    return this.http.post<any>(url, data, options);
  }

  getCustomAttributes(profileId: number, type = 'profiles'): Observable<CustomAttribute[]> {
    const url = `${OI_URL}/${type}/${profileId}/customAttributes`;
    return this.http.get<CustomAttribute[]>(url);
  }

  getDeviceCustomAttributes(
    profileId: number,
    mcDeviceId: string,
    type = 'profiles'
  ): Observable<CustomAttributeValue[]> {
    const url = `${OI_URL}/${type}/${profileId}/devices/${mcDeviceId}/customAttributes`;
    return this.http.get<CustomAttributeValue[]>(url);
  }

  setDeviceCustomAttributes(
    profileId: number,
    mcDeviceId: string,
    attributes: { [name: string]: string }
  ): Observable<void> {
    const url = `${OI_URL}/profiles/${profileId}/devices/${mcDeviceId}/customAttributes`;
    const json = JSON.stringify(attributes);
    return this.http.put<void>(url, json);
  }

  getDeviceInfo(mcDeviceId: string = null): Observable<DeviceDataResponse[]> {
    const url = `${OI_URL}/profiles/devices/${mcDeviceId}`;
    return this.http.get<DeviceDataResponse[]>(url);
  }

  getDeviceProfile(mcDeviceId: string = null): Observable<DeviceDataResponse[]> {
    const url = `${OI_URL}/profiles/devices/${mcDeviceId}`;
    return this.http.get<DeviceDataResponse[]>(url);
  }

  getBatchResult(): Observable<BatchImportJobResultModel> {
    const url = `${OI_URL}/batchimport/result`;
    return this.http.get<BatchImportJobResultModel>(url);
  }

  getShifts(): Observable<any> {
    const url = `${OI_URL}/batterypool/shifts`;
    return this.http.get<any>(url);
  }

  getCategories(): Observable<any> {
    const url = `${OI_URL}/apps/categories`;
    return this.http.get<any>(url);
  }

  getAppInfo(appId: string): Observable<any> {
    const url = `${OI_URL}/apps/info/${appId}`;
    return this.http.get<any>(url);
  }

  getWebAppInfo(webAppId: number): Observable<any> {
    const url = `${OI_URL}/webapp/info/${webAppId}`;
    return this.http.get<any>(url);
  }

  getWebCategories(): Observable<any> {
    const url = `${OI_URL}/webapp/categories`;
    return this.http.get<any>(url);
  }

  getMappings(): Observable<any> {
    const url = `${OI_URL}/apps/mappings`;
    return this.http.get<any>(url);
  }

  getWebMappings(): Observable<any> {
    const url = `${OI_URL}/webapp/webapplist`;
    return this.http.get<any>(url);
  }

  setShift(data): Observable<any> {
    const url = `${OI_URL}/batterypool/shifts`;
    return this.http.post<any>(url, data);
  }

  getShift(shiftId: number): Observable<any> {
    const url = `${OI_URL}/batterypool/shifts/${shiftId}`;
    return this.http.get(url);
  }

  deleteShift(shiftId: number): Observable<any> {
    const url = `${OI_URL}/batterypool/shifts/${shiftId}`;
    return this.http.delete(url);
  }

  setDecomissionStatus(data: SbDecommission): Observable<any> {
    const url = `${OI_URL}/batterypool/decommission`;
    return this.http.post<any>(url, data);
  }

  getCapacityConfig(): Observable<ThresholdConfig[]> {
    const url = `${OI_URL}/batterypool/config/capacity`;
    return this.http.get<any>(url);
  }

  updateCapacityConfig(data: ThresholdConfig[]) {
    const url = `${OI_URL}/batterypool/config/capacity`;
    return this.http.post<any>(url, data);
  }

  getCyclesConfig(): Observable<ThresholdConfig[]> {
    const url = `${OI_URL}/batterypool/config/cycles`;
    return this.http.get<any>(url);
  }

  updateCyclesConfig(data: ThresholdConfig[]) {
    const url = `${OI_URL}/batterypool/config/cycles`;
    return this.http.post<any>(url, data);
  }

  getTemperatureConfig(): Observable<TemperatureConfig> {
    const url = `${OI_URL}/batterypool/config/temperature`;
    return this.http.get<TemperatureConfig>(url);
  }

  updateTemperatureConfig(data: TemperatureConfig): Observable<any> {
    const url = `${OI_URL}/batterypool/config/temperature`;
    return this.http.post<any>(url, data);
  }

  getDischargeRateConfig(): Observable<ThresholdConfig[]> {
    const url = `${OI_URL}/batterypool/config/disrate`;
    return this.http.get<any>(url);
  }

  updateDischargeRateConfig(data: ThresholdConfig[]) {
    const url = `${OI_URL}/batterypool/config/disrate`;
    return this.http.post<any>(url, data);
  }

  getStrengthConfig(id: number): Observable<ThresholdConfig[]> {
    const url = `${OI_URL}/pages/config/strength/${id}`;
    return this.http.get<any>(url);
  }

  updateStrengthConfig(data: StrengthThresholdConfig[]) {
    const url = `${OI_URL}/pages/config/strength`;
    return this.http.post<any>(url, data);
  }

  updateAppCategories(categories: AppCategory[]): Observable<any> {
    const url = `${OI_URL}/apps/categories`;
    return this.http.post<any>(url, categories);
  }

  updateWebAppCategories(categories: WebAppCategory[]): Observable<any> {
    const url = `${OI_URL}/webapp/categories`;
    return this.http.post<any>(url, categories);
  }

  updateAppMappings(mappings: AppMappings[]): Observable<any> {
    const url = `${OI_URL}/apps/mappings`;
    return this.http.post<any>(url, mappings);
  }

  updateWebAppMappings(mappings: WebAppMapping[]): Observable<any> {
    const url = `${OI_URL}/webapp/webapplist`;
    return this.http.post<any>(url, mappings);
  }

  getDefaultAppMappings() {
    const url = `${OI_URL}/apps/mappings/default`;
    return this.http.get<any>(url);
  }

  getModelOverrideCapacity() {
    const url = `${OI_URL}/batterypool/ratedcapacity`;
    return this.http.get<any>(url);
  }

  setModelOverrideCapacity(capacities: RatedCapacity[]): Observable<any> {
    const url = `${OI_URL}/batterypool/ratedcapacity`;
    return this.http.post<any>(url, capacities);
  }

  getUtilizationProfiles(): Observable<UtilizationProfile[]> {
    return this.http.get<UtilizationProfile[]>(`${OI_URL}/utilization/profiles`);
  }

  getUtilizationMcGroups(instanceId: number, osId: number = 0): Observable<UtilizationMcGroup[]> {
    window.addTiming && window.addTiming(`rest:getUtilizationMcGroups:${instanceId}`);
    const url = `${OI_URL}/utilization/groups/${instanceId}/${osId}`;
    return this.http
      .get<UtilizationMcGroup[]>(url)
      .pipe(tap({ next: () => window.endTiming && window.endTiming(`rest:getUtilizationMcGroups:${instanceId}`) }));
  }

  getUtilizationStores(profileId: number): Observable<UtilizationStores[]> {
    window.addTiming && window.addTiming(`rest:getUtilizationStores:${profileId}`);
    const url = `${OI_URL}/utilization/stores/${profileId}`;
    return this.http
      .get<UtilizationStores[]>(url)
      .pipe(tap({ next: () => window.endTiming && window.endTiming(`rest:getUtilizationStores:${profileId}`) }));
  }

  updateUtilizationProfile(profile: UtilizationProfileRequest, bndInfo?: SharedTopicBundle) {
    const url = `${OI_URL}/utilization/profiles/update`;
    const body = {
      ...profile,
      profileConfig: { bId: bndInfo.bId },
    };
    if (profile.profileConfig?.advancedSettings) {
      body.profileConfig['advancedSettings'] = profile.profileConfig.advancedSettings;
    }
    return this.http.post<number>(url, body).pipe(
      map(profileId => ({
        ...profile,
        profileId: profileId,
        profileConfig: { bId: bndInfo.bId, advancedSettings: profile.profileConfig?.advancedSettings },
      }))
    );
  }

  deleteUtilizationProfile(profileId: number) {
    return this.http.delete(`${OI_URL}/utilization/profiles/${profileId}`);
  }

  getutilizationProfileById(profileId: number): Observable<UtilizationProfile> {
    return this.http.get<UtilizationProfile>(`${OI_URL}/utilization/profiles/${profileId}`);
  }

  importWallMappings(file: File): Observable<any> {
    window.addTiming && window.addTiming('rest:importWallMappings');
    const formData = new FormData();
    formData.append('uploadedFile', file, file.name);
    const url = `${OI_URL}/utilization/walls/csvUpload`;
    return this.http
      .post<any>(url, formData)
      .pipe(tap({ next: () => window.endTiming && window.endTiming('rest:importWallMappings') }));
  }

  getWallMappings(): Observable<UtilizationWallMappings[]> {
    return this.http.get<UtilizationWallMappings[]>(`${OI_URL}/utilization/walls`);
  }

  getCustomAttributesUtil(): Observable<UtilizationCustomAttributes[]> {
    return this.http.get<UtilizationCustomAttributes[]>(`${OI_URL}/utilization/customattributes`);
  }

  updateUtilizationConfig(customAttributeConfig: UtilizationCustomAttributeSelection) {
    const url = `${OI_URL}/utilization/config`;
    return this.http.post(url, customAttributeConfig);
  }

  getUtilizationConfig(): Observable<UtilizationCustomAttributeSelection> {
    const url = `${OI_URL}/utilization/config`;
    return this.http.get<UtilizationCustomAttributeSelection>(url);
  }
}
